import type { IslandTheme } from "@tiny-seas/shared";
import {
  Color3,
  MaterialPluginBase,
  type AbstractEngine,
  type BaseTexture,
  type MaterialDefines,
  type PBRMaterial,
  type Scene,
  type SubMesh,
  type UniformBuffer,
} from "@babylonjs/core";
import { FORTRESS_PROFILES, FORTRESS_ROCK_COLOR, SURFACE_PROFILES, type Materials } from "./materials.ts";
import { getSurfaceTextures, type SurfaceTextures } from "./surface-textures.ts";
import { TERRAIN } from "./themes.ts";

// These declarations are fixed across materials. Routing is part of the effect's
// defines, while palette values remain uniforms, so shader-cache reuse is safe.
const SAMPLER_DECLARATIONS = [0, 1, 2].map((index) => `
#if TERRAIN_MAP_COUNT > ${index}
uniform sampler2D terrainAlbedo${index};
uniform sampler2D terrainNormal${index};
uniform sampler2D terrainOrm${index};
#endif
`).join("");
const LAYER_ALIASES = ["SHORE", "INLAND", "CLIFF"].map((layer) =>
  [0, 1, 2].map((index) => `
#if TERRAIN_${layer}_MAP == ${index}
#define TERRAIN_${layer}_ALBEDO terrainAlbedo${index}
#define TERRAIN_${layer}_NORMAL terrainNormal${index}
#define TERRAIN_${layer}_ORM terrainOrm${index}
#endif
`).join("")
).join("");

const FRAGMENT_DEFINITIONS = `
varying vec3 vTerrainWeights;
varying vec2 vTerrainMacro;
${SAMPLER_DECLARATIONS}
${LAYER_ALIASES}
float terrainBlendedRoughness;
float terrainBlendedAO;

#if TERRAIN_FORTRESS
float terrainWeatherHash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}
float terrainWeatherNoise(vec2 p) {
    vec2 cell = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(mix(terrainWeatherHash(cell), terrainWeatherHash(cell + vec2(1.0, 0.0)), f.x),
        mix(terrainWeatherHash(cell + vec2(0.0, 1.0)),
            terrainWeatherHash(cell + vec2(1.0, 1.0)), f.x), f.y);
}
#endif
vec3 terrainDetailNormal(vec3 texel, float strength) {
    vec3 detail = texel * 2.0 - 1.0;
    // OpenGL +V points up the source image. Shared invertY=false uploads keep
    // its first row at V=0, so image-space +V runs opposite our metric V axis.
    detail.xy *= vec2(strength, -strength);
    return normalize(detail);
}

// Rotate an axis-projected normal into the actual world-space surface frame.
// Unlike blending axis normals directly, a flat normal map reproduces the
// geometric normal at every projection and does not flatten sloped ground.
vec3 terrainReorient(vec3 detailW, vec3 axisW, vec3 geometricW) {
    vec3 rotation = cross(axisW, geometricW);
    return normalize(detailW + cross(rotation, detailW)
        + cross(rotation, cross(rotation, detailW))
        / max(1.0 + dot(axisW, geometricW), 0.001));
}

vec3 terrainGroundNormal(vec3 detail, vec3 geometricW) {
    return terrainReorient(vec3(detail.x, detail.z, detail.y),
        vec3(0.0, 1.0, 0.0), geometricW);
}
`;

const FRAGMENT_SURFACE = `
vec3 terrainGeometricW = normalize(geometricNormalW);
vec3 terrainWeightsW = max(vTerrainWeights, vec3(0.0));
terrainWeightsW /= max(dot(terrainWeightsW, vec3(1.0)), 0.0001);
vec2 terrainMacroW = clamp(vTerrainMacro, vec2(0.0), vec2(1.0, 0.5));
float terrainWetness = 1.0 - smoothstep(0.1, 0.5, vPositionW.y);
#if TERRAIN_FORTRESS
// Patchy damp sand extends above the waterline, without a uniformly painted
// dark ring. The water owner supplies the submerged optical response.
float terrainWeather = terrainWeatherNoise(vPositionW.xz / 19.0);
float terrainDampHeight = 0.35 + 0.22 * terrainWeatherNoise(vPositionW.xz / 3.5);
terrainWetness = 1.0 - smoothstep(-0.15, terrainDampHeight, vPositionW.y);
#endif
vec2 terrainShoreUV = vPositionW.xz / 2.0;
vec2 terrainInlandUV = vPositionW.xz / 3.0;
vec3 terrainProjectionWeights = pow(abs(terrainGeometricW), vec3(4.0));
terrainProjectionWeights /= max(dot(terrainProjectionWeights, vec3(1.0)), 0.0001);
vec3 terrainAxisSign = mix(vec3(-1.0), vec3(1.0), step(vec3(0.0), terrainGeometricW));
vec2 terrainCliffUVX = vec2(vPositionW.z * terrainAxisSign.x, vPositionW.y) / 4.0;
vec2 terrainCliffUVY = vec2(vPositionW.x * terrainAxisSign.y, vPositionW.z) / 4.0;
vec2 terrainCliffUVZ = vec2(-vPositionW.x * terrainAxisSign.z, vPositionW.y) / 4.0;

vec3 terrainShoreDetail = toLinearSpace(texture2D(TERRAIN_SHORE_ALBEDO, terrainShoreUV).rgb);
vec3 terrainInlandDetail = toLinearSpace(texture2D(TERRAIN_INLAND_ALBEDO, terrainInlandUV).rgb);
vec3 terrainCliffDetail =
    toLinearSpace(texture2D(TERRAIN_CLIFF_ALBEDO, terrainCliffUVX).rgb) * terrainProjectionWeights.x
    + toLinearSpace(texture2D(TERRAIN_CLIFF_ALBEDO, terrainCliffUVY).rgb) * terrainProjectionWeights.y
    + toLinearSpace(texture2D(TERRAIN_CLIFF_ALBEDO, terrainCliffUVZ).rgb) * terrainProjectionWeights.z;
// Match the authored palette's altitude/noise blends, then decode tints before
// any texture modulation or material-layer blending in PBR's linear space.
vec3 terrainInlandTint = toLinearSpace(mix(
    mix(terrainGrassColor, terrainGrassDarkColor, terrainMacroW.x),
    terrainGrassDarkColor, terrainMacroW.y));
vec3 terrainShoreTint = toLinearSpace(mix(terrainSandColor, terrainWetSandColor, terrainWetness));
#if TERRAIN_FORTRESS
// Keep planted greenery distinct from earthy paths; colour changes are
// broad reflectance variation, not baked directional lighting.
terrainInlandTint *= mix(vec3(0.91, 0.94, 0.87), vec3(1.05, 1.035, 0.96), terrainWeather);
terrainShoreTint *= mix(0.96, 1.03, terrainWeather);
#endif
float terrainMacroJitter = 0.97 + 0.12 * terrainMacroW.y;
surfaceAlbedo = (
    terrainShoreDetail * terrainShoreTint * terrainWeightsW.x
    + terrainInlandDetail * terrainInlandTint * terrainWeightsW.y
    + terrainCliffDetail * toLinearSpace(terrainRockColor) * terrainWeightsW.z
) * terrainMacroJitter;

vec3 terrainShoreOrm = texture2D(TERRAIN_SHORE_ORM, terrainShoreUV).rgb;
vec3 terrainInlandOrm = texture2D(TERRAIN_INLAND_ORM, terrainInlandUV).rgb;
vec3 terrainCliffOrm =
    texture2D(TERRAIN_CLIFF_ORM, terrainCliffUVX).rgb * terrainProjectionWeights.x
    + texture2D(TERRAIN_CLIFF_ORM, terrainCliffUVY).rgb * terrainProjectionWeights.y
    + texture2D(TERRAIN_CLIFF_ORM, terrainCliffUVZ).rgb * terrainProjectionWeights.z;
float terrainShoreRoughness = mix(terrainShoreOrm.g * terrainLayerRoughness.x,
    terrainWetRoughness, terrainWetness);
terrainBlendedRoughness = clamp(dot(terrainWeightsW, vec3(terrainShoreRoughness,
    terrainInlandOrm.g * terrainLayerRoughness.y,
    terrainCliffOrm.g * terrainLayerRoughness.z)), 0.04, 1.0);
terrainBlendedAO = clamp(dot(terrainWeightsW,
    vec3(terrainShoreOrm.r, terrainInlandOrm.r, terrainCliffOrm.r)), 0.0, 1.0);

float terrainShoreNormalStrength = terrainLayerNormal.x;
#if TERRAIN_FORTRESS
terrainShoreNormalStrength *= mix(1.0, 0.38, terrainWetness);
#endif
vec3 terrainShoreNormalW = terrainGroundNormal(terrainDetailNormal(
    texture2D(TERRAIN_SHORE_NORMAL, terrainShoreUV).rgb, terrainShoreNormalStrength), terrainGeometricW);
vec3 terrainInlandNormalW = terrainGroundNormal(terrainDetailNormal(
    texture2D(TERRAIN_INLAND_NORMAL, terrainInlandUV).rgb, terrainLayerNormal.y), terrainGeometricW);
vec3 terrainCliffNormalX = terrainDetailNormal(
    texture2D(TERRAIN_CLIFF_NORMAL, terrainCliffUVX).rgb, terrainLayerNormal.z);
vec3 terrainCliffNormalY = terrainDetailNormal(
    texture2D(TERRAIN_CLIFF_NORMAL, terrainCliffUVY).rgb, terrainLayerNormal.z);
vec3 terrainCliffNormalZ = terrainDetailNormal(
    texture2D(TERRAIN_CLIFF_NORMAL, terrainCliffUVZ).rgb, terrainLayerNormal.z);
// All projection UVs are mirrored with their face sign. Transform each sampled
// normal to world space and reorient it before the normalized triplanar blend.
vec3 terrainCliffNormalW = normalize(
    terrainReorient(vec3(terrainCliffNormalX.z * terrainAxisSign.x,
        terrainCliffNormalX.y, terrainCliffNormalX.x * terrainAxisSign.x),
        vec3(terrainAxisSign.x, 0.0, 0.0), terrainGeometricW) * terrainProjectionWeights.x
    + terrainReorient(vec3(terrainCliffNormalY.x * terrainAxisSign.y,
        terrainCliffNormalY.z * terrainAxisSign.y, terrainCliffNormalY.y),
        vec3(0.0, terrainAxisSign.y, 0.0), terrainGeometricW) * terrainProjectionWeights.y
    + terrainReorient(vec3(-terrainCliffNormalZ.x * terrainAxisSign.z,
        terrainCliffNormalZ.y, terrainCliffNormalZ.z * terrainAxisSign.z),
        vec3(0.0, 0.0, terrainAxisSign.z), terrainGeometricW) * terrainProjectionWeights.z
);
normalW = normalize(terrainShoreNormalW * terrainWeightsW.x
    + terrainInlandNormalW * terrainWeightsW.y
    + terrainCliffNormalW * terrainWeightsW.z);
`;

const VERTEX_CODE = {
  CUSTOM_VERTEX_DEFINITIONS: `
attribute vec3 terrainWeights;
attribute vec2 terrainMacro;
varying vec3 vTerrainWeights;
varying vec2 vTerrainMacro;
`,
  CUSTOM_VERTEX_UPDATE_WORLDPOS: `
vTerrainWeights = terrainWeights;
vTerrainMacro = terrainMacro;
`,
};
const FRAGMENT_CODE = {
  CUSTOM_FRAGMENT_DEFINITIONS: FRAGMENT_DEFINITIONS,
  CUSTOM_FRAGMENT_BEFORE_LIGHTS: FRAGMENT_SURFACE,
  CUSTOM_FRAGMENT_UPDATE_METALLICROUGHNESS: "metallicRoughness = vec2(0.0, terrainBlendedRoughness);",
  // Babylon calls plugin injection after include expansion. Match the expanded
  // AO initialization, immediately before pbrBlockLightmapInit, not its include.
  "!aoOut=ambientOcclusionBlock\\([\\s\\S]*?\\);": "$0\naoOut.ambientOcclusionColor *= vec3(terrainBlendedAO);",
};
const COLOR_UNIFORMS = [
  "terrainSandColor", "terrainWetSandColor", "terrainGrassColor", "terrainGrassDarkColor", "terrainRockColor",
] as const;
const VECTOR_UNIFORMS = [...COLOR_UNIFORMS, "terrainLayerRoughness", "terrainLayerNormal"] as const;

class TerrainMaterialPlugin extends MaterialPluginBase {
  private readonly maps: readonly SurfaceTextures[];
  private readonly textures: readonly BaseTexture[];
  private readonly samplers: readonly string[];
  private readonly colors: readonly Color3[];
  private readonly roughness: readonly [number, number, number];
  private readonly normalStrength: readonly [number, number, number];
  private readonly wetRoughness: number;

  constructor(material: PBRMaterial, scene: Scene, theme: IslandTheme, skull = false) {
    const palette = skull
      ? { ...TERRAIN[theme], sand: "#dfc692", wetSand: "#a18d69", grass: "#82954c", grassDark: "#65783d", rock: FORTRESS_ROCK_COLOR }
      : TERRAIN[theme];
    const profiles = skull
      ? [FORTRESS_PROFILES.sand, FORTRESS_PROFILES.ground, FORTRESS_PROFILES.rock]
      : palette.maps.map((id) => SURFACE_PROFILES[id]);
    const layerMaps = profiles.map((profile) => profile.texture!);
    const ids = [...new Set(layerMaps)];
    super(material, "TerrainLayers", 200, {
      TERRAIN_MAP_COUNT: ids.length,
      TERRAIN_FORTRESS: skull,
      TERRAIN_SHORE_MAP: ids.indexOf(layerMaps[0]!),
      TERRAIN_INLAND_MAP: ids.indexOf(layerMaps[1]!),
      TERRAIN_CLIFF_MAP: ids.indexOf(layerMaps[2]!),
    });
    this.maps = ids.map((id) => getSurfaceTextures(scene, id));
    this.textures = this.maps.flatMap((maps) => [maps.albedo, maps.normal, maps.orm]);
    this.samplers = this.maps.flatMap((_, index) => [
      `terrainAlbedo${index}`, `terrainNormal${index}`, `terrainOrm${index}`,
    ]);
    this.colors = [palette.sand, palette.wetSand, palette.grass, palette.grassDark, palette.rock]
      .map((hex) => Color3.FromHexString(hex));
    this.roughness = profiles.map((profile) => profile.roughness) as [number, number, number];
    this.normalStrength = profiles.map((profile) => profile.normalStrength) as [number, number, number];
    this.wetRoughness = skull ? 0.4 : theme === "frost" ? 0.4 : 0.55;
    this.doNotSerialize = true;
    this._enable(true);
  }

  override getClassName(): string {
    return "TerrainMaterialPlugin";
  }

  override prepareDefinesBeforeAttributes(defines: MaterialDefines): void {
    defines._needNormals = true;
  }

  override getAttributes(attributes: string[]): void {
    attributes.push("terrainWeights", "terrainMacro");
  }

  override isReadyForSubMesh(_defines: MaterialDefines, _scene: Scene, _engine: AbstractEngine, subMesh: SubMesh): boolean {
    const mesh = subMesh.getMesh();
    if (!mesh.isVerticesDataPresent("terrainWeights") || !mesh.isVerticesDataPresent("terrainMacro")) {
      throw new Error(`terrain mesh ${mesh.name} requires terrainWeights and terrainMacro attributes`);
    }
    return this.textures.every((texture) => texture.isReady());
  }

  override getSamplers(samplers: string[]): void {
    samplers.push(...this.samplers);
  }

  override getUniforms() {
    return {
      ubo: [
        ...VECTOR_UNIFORMS.map((name) => ({ name, size: 3, type: "vec3" })),
        { name: "terrainWetRoughness", size: 1, type: "float" },
      ],
      fragment: `#ifndef UBO\n${VECTOR_UNIFORMS.map((name) => `uniform vec3 ${name};`).join("\n")}\nuniform float terrainWetRoughness;\n#endif`,
    };
  }

  override bindForSubMesh(uniformBuffer: UniformBuffer): void {
    for (let index = 0; index < COLOR_UNIFORMS.length; index++) {
      uniformBuffer.updateColor3(COLOR_UNIFORMS[index]!, this.colors[index]!);
    }
    uniformBuffer.updateFloat3("terrainLayerRoughness", ...this.roughness);
    uniformBuffer.updateFloat3("terrainLayerNormal", ...this.normalStrength);
    uniformBuffer.updateFloat("terrainWetRoughness", this.wetRoughness);
    for (let index = 0; index < this.textures.length; index++) {
      uniformBuffer.setTexture(this.samplers[index]!, this.textures[index]!);
    }
  }

  override getActiveTextures(activeTextures: BaseTexture[]): void {
    activeTextures.push(...this.textures);
  }

  override hasTexture(texture: BaseTexture): boolean {
    return this.textures.includes(texture);
  }

  override getCustomCode(shaderType: string): Record<string, string> | null {
    return shaderType === "vertex" ? VERTEX_CODE : shaderType === "fragment" ? FRAGMENT_CODE : null;
  }
}

const sceneMaterials = new WeakMap<Scene, Map<string, PBRMaterial>>();

/** Lit, scene-owned PBR terrain with immutable shared maps and metric world UVs. */
export function createTerrainMaterial(scene: Scene, mats: Materials, theme: IslandTheme, skull = false): PBRMaterial {
  let cache = sceneMaterials.get(scene);
  if (!cache) {
    cache = new Map();
    sceneMaterials.set(scene, cache);
  }
  const key = skull ? `${theme}:skull` : theme;
  let material = cache.get(key);
  if (!material) {
    material = mats.color(`terrain:${key}`, "#ffffff");
    material.metallic = 0;
    material.roughness = 1;
    material.forceIrradianceInFragment = true;
    material.metadata = {
      surfaceFamily: "terrain", theme, surfaceSet: skull ? "fortress" : "default",
      maps: skull
        ? [FORTRESS_PROFILES.sand.texture, FORTRESS_PROFILES.ground.texture, FORTRESS_PROFILES.rock.texture]
        : TERRAIN[theme].maps,
    };
    new TerrainMaterialPlugin(material, scene, theme, skull);
    cache.set(key, material);
  }
  return material;
}
